import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";

const WHATSAPP_NUMBER = "447340245823";

const Navigation = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/pricing", label: "Pricing" },
    { path: "/menu", label: "Menu" },
    { path: "/gallery", label: "Gallery" },
    { path: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  const openWhatsApp = () => {
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=Hi, I'm interested in booking Charlesworth Club for my event!`, "_blank");
  };

  return (
    <>
      <nav className="fixed top-0 w-full z-50 bg-black/60 backdrop-blur-xl border-b border-white/10 transition-all" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Brand Name */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <span className="text-xl font-black uppercase tracking-wider text-white">
                Charlesworth <span className="text-yellow-400">Club</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link ${isActive(link.path) ? "active" : ""}`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
              <button
                onClick={openWhatsApp}
                className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors"
                data-testid="whatsapp-btn"
              >
                <MessageCircle size={20} />
                <span className="text-sm font-semibold uppercase tracking-wider">Chat</span>
              </button>
              <Link
                to="/book"
                className="btn-primary text-sm py-3 px-6"
                data-testid="nav-book-now"
              >
                Book Now
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white p-2"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-button"
            >
              <Menu size={28} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <button
            className="absolute top-6 right-6 text-white"
            onClick={() => setMobileOpen(false)}
            data-testid="close-mobile-menu"
          >
            <X size={32} />
          </button>
          <div className="text-2xl font-black uppercase tracking-wider text-white mb-8">
            Charlesworth <span className="text-yellow-400">Club</span>
          </div>
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMobileOpen(false)}
              className={isActive(link.path) ? "text-yellow-400" : ""}
              data-testid={`mobile-nav-${link.label.toLowerCase()}`}
            >
              {link.label}
            </Link>
          ))}
          <button
            onClick={() => { openWhatsApp(); setMobileOpen(false); }}
            className="flex items-center gap-2 text-green-400 mt-4"
            data-testid="mobile-whatsapp-btn"
          >
            <MessageCircle size={24} />
            <span className="text-xl font-bold">Chat on WhatsApp</span>
          </button>
          <Link
            to="/book"
            onClick={() => setMobileOpen(false)}
            className="btn-primary mt-4"
            data-testid="mobile-nav-book-now"
          >
            Book Now
          </Link>
        </div>
      )}

      {/* Floating WhatsApp Button */}
      <button
        onClick={openWhatsApp}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110"
        data-testid="floating-whatsapp"
      >
        <MessageCircle size={28} />
      </button>
    </>
  );
};

export default Navigation;
